import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { useTenant } from "../context/TenantContext";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { config, landing, tenantLoading } = useTenant();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const cs = config?.color_secundario || "#1E3876";
  const cp = config?.color_primario || "#2853B9";
  const ca = config?.color_acento || "#3DA1E5";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success("Bienvenido a " + (config?.nombre || "Cubiq"));
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar sesion");
    } finally {
      setLoading(false);
    }
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6F8FA]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: cp }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F8FA] flex">
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center text-gray-600 mb-8 transition-colors hover:opacity-70">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al inicio
          </Link>
          <div className="cao-card p-8">
            <div className="text-center mb-8">
              {config?.logo_url && <img src={config.logo_url} alt={config.nombre} className="h-16 mx-auto mb-4" />}
              <h1 className="text-2xl font-bold" style={{ color: cs }}>Iniciar Sesion</h1>
              <p className="text-gray-600 mt-2">{landing?.login_subtitulo || "Ingrese sus credenciales"}</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="cao-label">Email</Label>
                <Input id="email" type="email" placeholder="su.email@ejemplo.com" className="cao-input"
                  value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required data-testid="login-email-input" />
              </div>
              <div>
                <Label htmlFor="password" className="cao-label">Contrasena</Label>
                <div className="relative">
                  <Input id="password" type={showPassword ? "text" : "password"} placeholder="********"
                    className="cao-input pr-10" value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full text-white rounded-full py-6 font-semibold hover:opacity-90"
                style={{ backgroundColor: cp }} disabled={loading} data-testid="login-submit-btn">
                {loading ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></span>
                    Ingresando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <LogIn className="w-5 h-5 mr-2" />
                    Iniciar Sesion
                  </span>
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                No tiene cuenta?{" "}
                <Link to="/register" className="font-medium hover:opacity-70" style={{ color: cp }}>Registrese aqui</Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(" + (landing?.login_imagen_url || "") + ")" }}>
          <div className="absolute inset-0" style={{ backgroundColor: cs + "B3" }}></div>
          <div className="absolute inset-0 flex items-center justify-center p-12">
            <div className="text-center text-white">
              {config?.logo_url && <img src={config.logo_url} alt="" className="w-20 h-20 mx-auto mb-6 brightness-0 invert" />}
              <h2 className="text-3xl font-bold mb-4">{config?.nombre || "Portal"}</h2>
              {landing?.login_mensaje_portal && <p className="max-w-md" style={{ color: ca + "CC" }}>{landing.login_mensaje_portal}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
